import type { GeneratorConfigV1, LeadCapture, LeadFieldConfig } from '../../types/generator-config';
import { defaultLeadCapture } from '../../lib/defaultConfig';

interface LeadCaptureSectionProps {
    config: GeneratorConfigV1;
    onChange: (updater: (prev: GeneratorConfigV1) => GeneratorConfigV1) => void;
}

const FIELD_LABELS: Record<keyof LeadCapture['fields'], string> = {
    name: 'Nom',
    email: 'Email',
    phone: 'Téléphone',
    company: 'Entreprise',
    message: 'Message libre',
};

const FIELD_ORDER: Array<keyof LeadCapture['fields']> = ['name', 'email', 'phone', 'company', 'message'];

export function LeadCaptureSection({ config, onChange }: LeadCaptureSectionProps) {
    const lc = config.leadCapture ?? defaultLeadCapture();

    const updateLead = (updater: (prev: LeadCapture) => LeadCapture) => {
        onChange((prev) => ({
            ...prev,
            leadCapture: updater(prev.leadCapture ?? defaultLeadCapture()),
        }));
    };

    const toggleEnabled = () => updateLead((prev) => ({ ...prev, enabled: !prev.enabled }));

    const setField = <K extends keyof LeadCapture>(key: K, value: LeadCapture[K]) =>
        updateLead((prev) => ({ ...prev, [key]: value }));

    const setSubField = (key: keyof LeadCapture['fields'], patch: Partial<LeadFieldConfig>) =>
        updateLead((prev) => ({
            ...prev,
            fields: {
                ...prev.fields,
                [key]: { ...prev.fields[key], ...patch },
            },
        }));

    return (
        <div className="editor-fields">
                <label className="field field-checkbox">
                    <input type="checkbox" checked={lc.enabled} onChange={toggleEnabled} />
                    <span>Activer le formulaire contact après le résumé</span>
                </label>

                {lc.enabled && (
                    <>
                        <label className="field">
                            <span className="field-label">Titre du formulaire</span>
                            <input
                                type="text"
                                value={lc.title}
                                onChange={(e) => setField('title', e.target.value)}
                                maxLength={120}
                            />
                        </label>

                        <label className="field">
                            <span className="field-label">Sous-titre / description</span>
                            <textarea
                                value={lc.description}
                                onChange={(e) => setField('description', e.target.value)}
                                rows={2}
                                maxLength={300}
                            />
                        </label>

                        <label className="field">
                            <span className="field-label">Libellé du bouton d'envoi</span>
                            <input
                                type="text"
                                value={lc.submitLabel}
                                onChange={(e) => setField('submitLabel', e.target.value)}
                                maxLength={60}
                            />
                        </label>

                        <div className="lc-fields-head">
                            <div className="field-label">Champs du formulaire</div>
                            <small className="impact-hint">
                                Activez uniquement ce dont vous avez besoin. Cochez « requis » pour
                                empêcher l'envoi si le champ est vide.
                            </small>
                        </div>

                        <div className="lc-fields-grid">
                            <div className="lc-fields-grid-head">
                                <span>Champ</span>
                                <span>Afficher</span>
                                <span>Requis</span>
                            </div>
                            {FIELD_ORDER.map((k) => {
                                const f = lc.fields[k];
                                return (
                                    <div key={k} className="lc-field-row">
                                        <span className="lc-field-name">{FIELD_LABELS[k]}</span>
                                        <label className="lc-toggle">
                                            <input
                                                type="checkbox"
                                                checked={f.enabled}
                                                onChange={(e) => setSubField(k, { enabled: e.target.checked })}
                                            />
                                        </label>
                                        <label className="lc-toggle">
                                            <input
                                                type="checkbox"
                                                checked={f.required}
                                                disabled={!f.enabled}
                                                onChange={(e) => setSubField(k, { required: e.target.checked })}
                                            />
                                        </label>
                                    </div>
                                );
                            })}
                        </div>

                        <label className="field">
                            <span className="field-label">
                                Mention RGPD / consentement (optionnelle)
                            </span>
                            <textarea
                                value={lc.consentText ?? ''}
                                onChange={(e) => setField('consentText', e.target.value || undefined)}
                                rows={2}
                                placeholder="En envoyant ce formulaire, vous acceptez d'être recontacté(e)…"
                                maxLength={400}
                            />
                            <small className="impact-hint">
                                Si renseignée, une case à cocher obligatoire sera affichée à côté du bouton.
                            </small>
                        </label>

                        <div className="lc-success-grid">
                            <label className="field">
                                <span className="field-label">Titre écran de confirmation</span>
                                <input
                                    type="text"
                                    value={lc.successTitle}
                                    onChange={(e) => setField('successTitle', e.target.value)}
                                    maxLength={120}
                                />
                            </label>
                            <label className="field">
                                <span className="field-label">Message de confirmation</span>
                                <textarea
                                    value={lc.successMessage}
                                    onChange={(e) => setField('successMessage', e.target.value)}
                                    rows={2}
                                    maxLength={300}
                                />
                            </label>
                        </div>
                    </>
                )}
        </div>
    );
}
